/**
 * Refer & Earn: the copy, and the figures to show before the server answers.
 *
 * ---------------------------------------------------------------------------
 * THE REFERRAL BACKEND EXISTS NOW. THIS FILE IS NO LONGER THE SOURCE.
 * ---------------------------------------------------------------------------
 * The note that stood here said there was none, and that the code shown was
 * DERIVED FROM THE PHONE NUMBER. Both were true when it was written and are
 * both false now: module 16 mints a real `ReferralCode`, attributes a signup
 * to it, and credits both sides when the referee's first booking completes.
 * A derived code would be shared and then refused, with the referrer never
 * told why.
 *
 * `services/referrals.ts` and `hooks/useReferrals.ts` are the source. What is
 * left here is copy - the three-step strip, the share sentence - plus reward
 * figures used ONLY until `ReferralSummary` lands. Nothing here is fetched,
 * counted, or trusted over the server.
 *
 * What was never faked, and still is not: no "₹450 earned" appears for a new
 * account. Invented earnings are a promise of real money, and the empty state
 * says the truthful thing instead.
 */

/**
 * The reward amounts, IN COINS, and only until the server answers.
 *
 * `ReferralSummary.refereeCoins` / `.referrerCoins` are the real figures -
 * ops sets them in Settings and they can change without an app release, so
 * anything compiled in here is a guess that goes stale. These exist for the
 * moment before the summary lands and for `referralMessage` below.
 *
 * They said 100 and 100 "in rupees" while the server's defaults were 200 and
 * 100 in coins, so the screen opened on a figure that was wrong and then
 * corrected itself - which reads as a bug even though the second number was
 * right. Matched to the server's fallbacks now, and named for what they are.
 */

/** Coins the friend gets, once their first booking completes. */
export const FRIEND_REWARD = 100;

/** Coins the referrer gets, at the same moment. */
export const REFERRER_REWARD = 200;

/**
 * How many friends can be rewarded per account.
 *
 * NOT RENDERED, and out of step on purpose rather than by accident: the real
 * cap is `referral.maxPerReferrer`, which ops can change, and the server
 * refuses a code past it with a message that says so. Kept only because a
 * capped account still has to be told why its code stopped paying.
 */
export const REFERRAL_CAP = 10;

export type ReferralStatus = 'joined' | 'booked' | 'paid';

export type Referral = {
  id: string;
  /** First name only. The full contact belongs to them, not to this screen. */
  name: string;
  /** When they joined, already formatted. */
  when: string;
  status: ReferralStatus;
  /** Credited only once `status` is 'paid'. Zero before that. */
  earned: number;
};

/** What each status means, in the customer's terms rather than the system's. */
export const referralStatusLabel: Record<ReferralStatus, string> = {
  joined: 'Signed up',
  booked: 'Booked their first service',
  paid: 'Reward credited',
};

export type ReferralSummary = {
  /** The code to share. Stable per account - see the file note. */
  code: string;
  /** Everyone who has used it. Empty for a new account, and that is normal. */
  referrals: Referral[];
  /** Rupees actually credited. Never a projection of what might be earned. */
  earned: number;
  /**
   * How many more friends can still be rewarded.
   *
   * NOT RENDERED. The line quoting it under the share button is gone - on a
   * new account it read "10 more friends can still earn you a reward", which
   * announces a cap to somebody who has not referred anybody yet. Kept
   * because the cap is real and a full account still has to be told why the
   * code has stopped paying out.
   */
  remaining: number;
};

/**
 * The code for an account.
 *
 * `HOMI` plus the last four digits of the phone number, which is stable, is
 * unique enough among a small user base, and - the part that matters - is not
 * something a person has to read character by character off a screen. A real
 * one is minted server-side and checked for collisions.
 *
 * Signed out there is no account to key on, so the caller gets the programme's
 * terms with no code at all rather than a placeholder that cannot be redeemed.
 */
export const referralCode = (phone: string | null): string =>
  phone && phone.length >= 4 ? `HOMI${phone.slice(-4)}` : '';

/**
 * Everything the Refer & Earn page renders, for one account.
 *
 * `referrals` is empty here on purpose. There is nothing to read them from,
 * and a seeded list of names would tell someone that strangers had joined
 * under their code - which is a lie about other people, not just about the
 * app.
 */
export const referralFor = (phone: string | null): ReferralSummary => {
  const referrals: Referral[] = [];
  return {
    code: referralCode(phone),
    referrals,
    earned: referrals
      .filter(item => item.status === 'paid')
      .reduce((total, item) => total + item.earned, 0),
    remaining: Math.max(0, REFERRAL_CAP - referrals.length),
  };
};

/** The three steps, for the "How it works" strip. */
/*
 * The three steps, each with a glyph.
 *
 * `icon` is typed loosely as a string rather than imported from the icon set:
 * this file is data and has no other reason to depend on a component. The
 * screen narrows it back to `IconName` where it renders, so a typo is still
 * caught at the call site.
 */
export const referralSteps: Array<{
  title: string;
  /**
   * The long form of the step.
   *
   * NOT RENDERED. The strip shows titles only - three columns of small print
   * under three icons was more reading than a glance-level sequence earns,
   * and "Share / They sign up / You earn" already tells the story. Kept
   * because it is the copy any longer-form explanation would reuse, and
   * because it is where the reward amounts are spelled out.
   */
  detail: string;
  icon: string;
}> = [
  {
    title: 'Share',
    detail: 'Send your code to friends.',
    icon: 'share',
  },
  {
    title: 'They sign up',
    detail: `They enter your code, then book their first service.`,
    icon: 'users',
  },
  {
    title: 'You earn',
    detail:
      `${REFERRER_REWARD} coins land in your wallet once that first job is ` +
      `complete - and they get ${FRIEND_REWARD}.`,
    icon: 'gift',
  },
];

/**
 * The message that goes out when the code is shared.
 *
 * A FALLBACK. `ReferralSummary.shareMessage` is what actually gets shared -
 * the server knows the current reward, this file knows what it was compiled
 * with. This is what goes out if the summary has not loaded yet.
 *
 * It used to promise "₹100 off your first booking", which was wrong twice
 * over: nothing comes off the first booking - it is paid in full and the
 * coins arrive when it COMPLETES - and coins are not rupees off a bill, they
 * are spendable up to a percentage of a later one.
 */
export const referralMessage = (code: string): string =>
  `Sign up for Homingo with my code ${code} - home services from cleaning to ` +
  `carpentry, at your door - and get ${FRIEND_REWARD} Homingo Coins once ` +
  `your first booking is complete.`;

/**
 * The terms of the programme.
 *
 * ---------------------------------------------------------------------------
 * NOT RENDERED ANYWHERE RIGHT NOW
 * ---------------------------------------------------------------------------
 * The Refer & Earn page dropped its Terms section, so nothing reads this at
 * the moment. Kept rather than deleted because the rules themselves have not
 * changed - they are what the reward actually depends on, and they are the
 * copy a real programme has to show somewhere before anyone shares a code.
 * The most likely home is a link from the offer card rather than a block at
 * the foot of the page.
 *
 * Short and specific, deliberately. "T&Cs apply" with nothing behind it is
 * what makes a referral programme feel like a trick.
 */
export const referralTerms: string[] = [
  `Your friend must be new to Homingo and apply the code on their first booking.`,
  `₹${REFERRER_REWARD} is credited to your wallet once their first job is completed and paid for.`,
  `Rewards are capped at ${REFERRAL_CAP} friends per account.`,
  `A cancelled or refunded first booking does not earn a reward.`,
];
